/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../Contexts/AuthContext'
import { subscribeToScreamRoom, postToScreamRoom, subscribeToPresence } from '../../services/screamRoom'
import { feelsForBucket, searchFeels, BUCKETS } from '../../services/feelsApi'

// Scream Room — a single shared real-time chat where internal people post text
// and throw Feels in. Anyone can come and go; everyone sees the same room.

// Feel accessors — primary shape is the /v1 `Feel` (id, song.name, artist.name,
// lyric.text, media.{poster,video}). Messages posted before the /v1 port are
// still in today's Redis room with the legacy low-level shape, so each accessor
// falls back to the old paths until the room's daily expiry flushes them.
function feelArtist(feel) {
  return feel?.artist?.name || feel?.song_artist_name || ''
}

function feelTitle(feel) {
  if (!feel) return ''
  const artist = feelArtist(feel)
  const song = feel.song?.name || feel.song_name || feel.name || ''
  if (artist && song) return `${artist} — ${song}`
  return artist || song
}

function feelLyrics(feel) {
  return feel?.lyric?.text || feel?.lyrics || ''
}

function feelVideoUrl(feel) {
  return feel?.media?.video || feel?.preview?.video?.video?.url || ''
}

function feelPosterUrl(feel) {
  return feel?.media?.poster || feel?.preview?.image?.image?.url || ''
}

// Same playback model as the Feels Drawer chat: each Feel plays (muted) and only
// the ACTIVE one is unmuted; the newest message auto-becomes active, the rest
// mute. Tapping a Feel toggles its sound (so any/all can be muted).
function Message({ msg, active, onToggle }) {
  const videoUrl = feelVideoUrl(msg.feel)
  const ref = useRef(null)

  useEffect(() => {
    const v = ref.current
    if (!v) return
    v.muted = !active
    if (active) {
      const p = v.play()
      if (p && p.catch) p.catch(() => {})
    } else {
      // Pause inactive Feels so chat history doesn't decode many videos at once.
      v.pause()
      v.currentTime = 0
    }
  }, [active])

  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
        {msg.author || 'anon'}
      </div>
      {msg.text ? (
        <div style={{ margin: '2px 0', whiteSpace: 'pre-wrap' }}>{msg.text}</div>
      ) : null}
      {msg.feel ? (
        <div
          style={{
            marginTop: 6,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            background: '#fafafa',
            border: '1px solid #eee',
            borderRadius: 8,
            padding: 8,
            maxWidth: 520,
          }}
        >
          {videoUrl ? (
            <div
              onClick={onToggle}
              title={active ? 'Tap to mute' : 'Tap for sound'}
              style={{
                position: 'relative',
                width: 140,
                flexShrink: 0,
                borderRadius: 6,
                overflow: 'hidden',
                background: '#000',
                cursor: 'pointer',
              }}
            >
              <video
                ref={ref}
                src={videoUrl}
                autoPlay
                loop
                muted={!active}
                playsInline
                style={{ width: '100%', display: 'block' }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: 6,
                  right: 6,
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  fontSize: 11,
                }}
              >
                {active ? '\u{1F50A}' : '\u{1F507}'}
              </div>
            </div>
          ) : null}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {feelTitle(msg.feel)}
            </div>
            {feelLyrics(msg.feel) ? (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  color: '#666',
                  whiteSpace: 'pre-line',
                }}
              >
                {feelLyrics(msg.feel)}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

// Presence roster — one chip per person who's shown up today. Green bulb = online
// (currently on the page, heartbeating); red bulb = away (navigated off) but their
// name stays so you can see who's around. Lights back up when they return.
function PresenceBar({ roster }) {
  if (!roster || roster.length === 0) return null
  const onlineCount = roster.filter(p => p.online).length

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 12 }}>
      <span style={{ fontSize: 12, color: '#888', marginRight: 2 }}>
        {onlineCount} here now
      </span>
      {roster.map(p => (
        <span
          key={p.author}
          title={p.online ? 'Online' : 'Away'}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '3px 10px 3px 7px',
            borderRadius: 999,
            background: p.online ? '#eafaf0' : '#f5f5f5',
            border: `1px solid ${p.online ? '#bdebcd' : '#e3e3e3'}`,
            fontSize: 12,
            color: p.online ? '#1f7a44' : '#9a9a9a',
          }}
        >
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: p.online ? '#2ecc71' : '#e74c3c',
              boxShadow: p.online ? '0 0 6px rgba(46,204,113,0.75)' : 'none',
              flex: '0 0 auto',
            }}
          />
          {p.author}
        </span>
      ))}
    </div>
  )
}

export default function ScreamRoom() {
  const { getCurrentUser } = useAuth()
  const user = getCurrentUser()
  const author = user?.email || user?.displayName || user?.name || 'anon'

  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const [pickerFeels, setPickerFeels] = useState([])
  const [pickerError, setPickerError] = useState(null)
  const [pickerBucket, setPickerBucket] = useState(BUCKETS[0])
  const [pickerQuery, setPickerQuery] = useState('')
  const [pickerLoading, setPickerLoading] = useState(false)
  const [activeId, setActiveId] = useState(null)
  const [roster, setRoster] = useState([])
  const listEndRef = useRef(null)
  const prevLastIdRef = useRef(null)

  const toggleActive = id => setActiveId(cur => (cur === id ? null : id))

  useEffect(() => {
    const unsubscribe = subscribeToScreamRoom(
      setMessages,
      () => setUnavailable(true)
    )
    return unsubscribe
  }, [])

  // Presence: heartbeat while this page is open so others see me online (green);
  // navigating away stops the beat and turns my bulb red (name stays). The roster
  // refreshes on each heartbeat.
  useEffect(() => subscribeToPresence(setRoster, () => {}), [])

  // Picker Feels: a free-text search when there's a query (debounced), otherwise
  // browse the current mood bucket. External Feels Sharing API only — a failure is
  // shown so we know the integration is broken.
  useEffect(() => {
    let active = true
    const q = pickerQuery.trim()
    setPickerLoading(true)
    setPickerError(null)

    const run = () => {
      (q ? searchFeels(q) : feelsForBucket(pickerBucket))
        .then(list => {
          if (!active) return
          setPickerFeels(Array.isArray(list) ? list : [])
          setPickerLoading(false)
        })
        .catch(e => {
          if (!active) return
          setPickerFeels([])
          setPickerError(e.message || 'Failed to load Feels from the external API.')
          setPickerLoading(false)
        })
    }

    // Debounce typing; browse-by-bucket loads immediately.
    const timer = setTimeout(run, q ? 300 : 0)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [pickerBucket, pickerQuery])

  // Auto-scroll to the newest message whenever the list grows.
  useEffect(() => {
    if (listEndRef.current) {
      listEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [messages])

  // When a brand-new message arrives, make its Feel the unmuted/active one (and
  // mute the rest) — mirrors the Feels Drawer chat playback.
  useEffect(() => {
    if (!messages.length) return
    const last = messages[messages.length - 1]
    if (last.id !== prevLastIdRef.current) {
      prevLastIdRef.current = last.id
      if (last.feel) setActiveId(last.id)
    }
  }, [messages])

  const sendText = async () => {
    const trimmed = text.trim()
    if (!trimmed) return
    setText('')
    try {
      await postToScreamRoom({ text: trimmed, author })
    } catch (err) {
      setUnavailable(true)
    }
  }

  const sendFeel = async feel => {
    const trimmed = text.trim()
    setText('')
    setPickerOpen(false)
    try {
      await postToScreamRoom({ text: trimmed, feel, author })
    } catch (err) {
      setUnavailable(true)
    }
  }

  const onInputKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendText()
    }
  }

  return (
    <div
      style={{
        padding: 28,
        maxWidth: 720,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 48px)',
      }}
    >
      <style>{`
        @keyframes feelsShimmer {
          0% { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        @keyframes feelsSpin { to { transform: rotate(360deg); } }
      `}</style>
      <h2 style={{ marginBottom: 2, fontSize: 34, fontWeight: 900, letterSpacing: '-1px' }}>
        <span
          style={{
            backgroundImage:
              'linear-gradient(90deg, #ff4ecd, #a04cff, #4cc9ff, #ff4ecd)',
            backgroundSize: '300% 100%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            animation: 'feelsShimmer 4s linear infinite',
            textShadow: '0 0 22px rgba(160, 76, 255, 0.35)',
          }}
        >
          Scream Room
        </span>{' '}
        🗣️
      </h2>
      <p style={{ color: '#888', marginTop: 0, marginBottom: 14 }}>
        Scream your thoughts into this room!
      </p>

      <PresenceBar roster={roster} />

      {unavailable ? (
        <div
          style={{
            background: '#fff7e6',
            border: '1px solid #ffe1a8',
            color: '#8a5a00',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          Can&apos;t reach the Scream Room service right now — messages won&apos;t
          load or send until the backend API is available.
        </div>
      ) : null}

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid #eee',
          borderRadius: 10,
          padding: '4px 16px',
          marginBottom: 16,
          background: '#fff',
        }}
      >
        {messages.length === 0 ? (
          <p style={{ color: '#aaa', padding: '16px 0' }}>
            No one has screamed yet. Be the first.
          </p>
        ) : (
          messages.map(msg => (
            <Message
              key={msg.id}
              msg={msg}
              active={msg.id === activeId}
              onToggle={() => toggleActive(msg.id)}
            />
          ))
        )}
        <div ref={listEndRef} />
      </div>

      {pickerOpen ? (
        <div
          style={{
            marginBottom: 16,
            border: '1px solid #e3e3e3',
            borderRadius: 12,
            padding: 18,
            background: '#fff',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <strong style={{ fontSize: 12, color: '#333', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pick a Feel
            </strong>
            <button
              type="button"
              onClick={() => { setPickerOpen(false); setPickerQuery('') }}
              aria-label="Close"
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#aaa', fontSize: 16, lineHeight: 1, padding: 2 }}
            >
              ✕
            </button>
          </div>

          {/* Full search across all Feels (Gebra) */}
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <input
              type="text"
              value={pickerQuery}
              onChange={e => setPickerQuery(e.target.value)}
              placeholder="Search all Feels — artist, song, lyric…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '11px 34px 11px 14px',
                borderRadius: 10,
                border: '1px solid #ddd',
                fontSize: 14,
              }}
            />
            {/* Work-in-progress indicator: spinner while searching, clear-✕ otherwise */}
            <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
              {pickerLoading ? (
                <span
                  aria-label="Searching"
                  style={{
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    border: '2px solid #e1306c',
                    borderTopColor: 'transparent',
                    display: 'inline-block',
                    animation: 'feelsSpin 0.6s linear infinite',
                  }}
                />
              ) : pickerQuery ? (
                <button
                  type="button"
                  onClick={() => setPickerQuery('')}
                  aria-label="Clear search"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#aaa', fontSize: 15, lineHeight: 1, padding: 0 }}
                >
                  ✕
                </button>
              ) : null}
            </div>
          </div>

          {/* Mood buckets — quick browse (dimmed while searching) */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, opacity: pickerQuery ? 0.5 : 1 }}>
            {BUCKETS.map(b => {
              const selected = !pickerQuery && b === pickerBucket
              return (
                <button
                  key={b}
                  type="button"
                  onClick={() => { setPickerQuery(''); setPickerBucket(b) }}
                  style={{
                    padding: '4px 11px',
                    border: `1px solid ${selected ? '#e1306c' : '#ddd'}`,
                    background: selected ? '#e1306c' : '#fff',
                    color: selected ? '#fff' : '#444',
                    borderRadius: 999,
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 12,
                  }}
                >
                  {b}
                </button>
              )
            })}
          </div>

          {/* What's being shown — turns into a live "Searching…" status */}
          <div style={{ fontSize: 11.5, color: pickerLoading ? '#e1306c' : '#999', marginBottom: 10, transition: 'color 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}>
            {pickerLoading ? (
              <>
                <span style={{ width: 11, height: 11, borderRadius: '50%', border: '2px solid #e1306c', borderTopColor: 'transparent', display: 'inline-block', animation: 'feelsSpin 0.6s linear infinite' }} />
                {pickerQuery.trim() ? `Searching “${pickerQuery.trim()}”…` : 'Loading…'}
              </>
            ) : pickerQuery.trim()
              ? `Search: “${pickerQuery.trim()}” · ${pickerFeels.length} result${pickerFeels.length === 1 ? '' : 's'}`
              : `Browsing: ${pickerBucket}`}
          </div>

          {/* Results grid — dims while a search is in flight */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'flex-start',
              gap: 10,
              maxHeight: 280,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 10,
              padding: 14,
              background: '#fafafa',
              opacity: pickerLoading ? 0.45 : 1,
              transition: 'opacity 0.15s',
            }}
          >
          {pickerLoading && pickerFeels.length === 0 ? (
            <p style={{ color: '#888', margin: 0, fontSize: 13, fontStyle: 'italic', flex: '1 0 100%' }}>
              Loading Feels…
            </p>
          ) : !pickerLoading && pickerError ? (
            <p style={{ color: '#c0392b', margin: 0, fontSize: 13, flex: '1 0 100%' }}>
              ⚠️ {pickerError}
            </p>
          ) : !pickerLoading && pickerFeels.length === 0 ? (
            <p style={{ color: '#888', margin: 0, fontSize: 13, flex: '1 0 100%' }}>
              {pickerQuery.trim() ? 'No Feels match your search.' : 'No Feels available.'}
            </p>
          ) : null}
          {pickerFeels.map(feel => (
            <button
              key={feel.id}
              type="button"
              onClick={() => sendFeel(feel)}
              title={feelTitle(feel)}
              style={{
                position: 'relative',
                flex: '0 0 auto',
                width: 92,
                height: 92,
                border: '1px solid #e3e3e3',
                padding: 0,
                margin: 0,
                cursor: 'pointer',
                borderRadius: 8,
                overflow: 'hidden',
                background: '#000',
                lineHeight: 0,
              }}
            >
              <img
                src={feelPosterUrl(feel)}
                alt={feelTitle(feel)}
                loading="lazy"
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: '2px 4px',
                  fontSize: 9,
                  lineHeight: 1.2,
                  color: '#fff',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.82))',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textAlign: 'left',
                }}
              >
                {feelArtist(feel)}
              </span>
            </button>
          ))}
        </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 14, borderTop: '1px solid #ececec' }}>
        {/* Primary action: throw a Feel — we talk primarily with Feels. */}
        <button
          type="button"
          onClick={() => setPickerOpen(open => !open)}
          style={{
            padding: '13px 18px',
            borderRadius: 12,
            border: 'none',
            cursor: 'pointer',
            fontSize: 16,
            fontWeight: 800,
            color: '#fff',
            backgroundImage: 'linear-gradient(90deg, #ff4ecd, #a04cff, #4cc9ff, #ff4ecd)',
            backgroundSize: '300% 100%',
            animation: 'feelsShimmer 5s linear infinite',
            boxShadow: '0 4px 16px rgba(160, 76, 255, 0.35)',
            opacity: pickerOpen ? 0.92 : 1,
          }}
        >
          🗣️ {pickerOpen ? 'Close Feels' : 'Add a Feel'}
        </button>

        {/* Secondary: an optional small comment. */}
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="add a comment…"
            style={{
              flex: 1,
              padding: '6px 10px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: 12.5,
              color: '#555',
              background: '#fcfcfc',
            }}
          />
          <button
            type="button"
            onClick={sendText}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              background: '#fff',
              color: '#666',
              fontSize: 12.5,
              cursor: 'pointer',
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  )
}
